'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var reactI18next = require('react-i18next');
var cx = require('classnames');
var Picker = require('react-datepicker');
var isBefore = require('date-fns/isBefore');
var isAfter = require('date-fns/isAfter');
var enUS = require('date-fns/locale/en-US');
var useDefaultInternalState = require('../../hooks/useDefaultInternalState.js');
var Label = require('../alloy-input/Label.js');
var parse = require('date-fns/parse');
var utils = require('./utils.js');
var ErrorMessage = require('../alloy-input/ErrorMessage.js');
var datePicker_module = require('./date-picker.module.scss.js');
var CalendarIcon = require('../alloy-icon/CalendarIcon.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var cx__default = /*#__PURE__*/_interopDefault(cx);
var Picker__default = /*#__PURE__*/_interopDefault(Picker);
var isBefore__default = /*#__PURE__*/_interopDefault(isBefore);
var isAfter__default = /*#__PURE__*/_interopDefault(isAfter);
var enUS__default = /*#__PURE__*/_interopDefault(enUS);
var parse__default = /*#__PURE__*/_interopDefault(parse);

var DEFAULT_LOCALE = enUS__default.default;
var DEFAULT_DATE_FORMAT = 'P';
var INVALID_DATE = 'INVALID_DATE';
var DatePicker = function (_a) {
    var _b;
    var children = _a.children, _c = _a.className, className = _c === void 0 ? '' : _c, date = _a.date, _d = _a.dateFormat, dateFormat = _d === void 0 ? DEFAULT_DATE_FORMAT : _d, error = _a.error, _e = _a.helpText, helpText = _e === void 0 ? '' : _e, helpTextClassname = _a.helpTextClassname, _f = _a.hideLabel, hideLabel = _f === void 0 ? false : _f, id = _a.id, label = _a.label, _g = _a.locale, locale = _g === void 0 ? DEFAULT_LOCALE : _g, maxDate = _a.maxDate, _h = _a.maxDateErrorMessage, maxDateErrorMessage = _h === void 0 ? INVALID_DATE : _h, minDate = _a.minDate, _j = _a.minDateErrorMessage, minDateErrorMessage = _j === void 0 ? INVALID_DATE : _j, onBlur = _a.onBlur, onError = _a.onError, onFocus = _a.onFocus, setDate = _a.setDate, setError = _a.setError, showOptional = _a.showOptional, showRequired = _a.showRequired, validator = _a.validator, value = _a.value, _k = _a.popperModifiers, popperModifiers = _k === void 0 ? [] : _k, labelClassName = _a.labelClassName, props = tslib.__rest(_a, ["children", "className", "date", "dateFormat", "error", "helpText", "helpTextClassname", "hideLabel", "id", "label", "locale", "maxDate", "maxDateErrorMessage", "minDate", "minDateErrorMessage", "onBlur", "onError", "onFocus", "setDate", "setError", "showOptional", "showRequired", "validator", "value", "popperModifiers", "labelClassName"]);
    var dateValue = value || date; // accept value or date prop
    var ref = react.useRef(null);
    var _l = useDefaultInternalState.default(dateValue, setDate), dateState = _l[0], setDateState = _l[1];
    var _m = react.useState(), rawDate = _m[0], setRawDate = _m[1];
    var _o = react.useState(), pendingError = _o[0], setPendingError = _o[1];
    var _p = useDefaultInternalState.default(error, setError), errorState = _p[0], setErrorState = _p[1];
    var t = reactI18next.useTranslation()[0];
    // these useEffects are used only when validating min and max date. If a date is out of range
    // we override default behavior and clear the field. We need to wait until it is complete before
    // notifying the parent or updating the the local date state. All validation to these point will have run. We can then
    // manually set the error message that we desire.
    react.useEffect(function () {
        if (pendingError && rawDate === null) {
            // second param is false to prevent validator from running
            setDateState(null, false);
        }
    }, [dateState, pendingError, rawDate, setDateState, setErrorState]);
    react.useEffect(function () {
        if (pendingError && dateState === null) {
            setErrorState(pendingError);
            setPendingError('');
        }
    }, [dateState, pendingError, rawDate, setErrorState]);
    var handleChangeRaw = function (_a) {
        var _b = _a.target.value, value = _b === void 0 ? '' : _b;
        var normalizedValue = utils.normalizeDate(value, dateFormat);
        setRawDate(normalizedValue);
        if (ref.current) {
            ref.current.input.value = normalizedValue;
        }
    };
    var handleChange = function (newDate) {
        setDateState(newDate);
    };
    var handleBlur = function (e) {
        var result = '';
        // this will trigger validation against typed value
        if (rawDate) {
            // convert string to Date before using date-fns
            var converted = parse__default.default(rawDate, dateFormat, new Date());
            if (minDate && isBefore__default.default(converted, minDate)) {
                result = t(minDateErrorMessage);
            }
            if (maxDate && isAfter__default.default(converted, maxDate)) {
                result = t(maxDateErrorMessage);
            }
            // clear input if there is a max or min error
            // Set pending error message to state for use after all other validation completes
            if (result) {
                setPendingError(result);
                setRawDate(null);
            }
        }
        onBlur && onBlur(e);
        // run regular validator if an error doesn't already exist
        if (validator && !result) {
            result = validator(dateState);
            setErrorState(result);
        }
    };
    var handleFocus = function (e) {
        onFocus && onFocus(e);
        setErrorState();
    };
    return (jsxRuntime.jsxs("div", tslib.__assign({ className: "mb-3 ".concat(datePicker_module.default['date-picker'], " ").concat(className) }, { children: [label && (jsxRuntime.jsx(Label.default, tslib.__assign({ className: cx__default.default((_b = {},
                    _b[datePicker_module.default['date-picker__label']] = true,
                    _b['d-none'] = hideLabel,
                    _b), labelClassName), fieldId: id, showOptional: showOptional, showRequired: showRequired }, { children: label }))), jsxRuntime.jsxs("div", tslib.__assign({ className: "".concat(datePicker_module.default['input-wrapper'], " w-100") }, { children: [jsxRuntime.jsx(Picker__default.default, tslib.__assign({}, props, { minDate: minDate, maxDate: maxDate, autoComplete: "off", id: id, locale: locale, selected: dateState, dateFormat: dateFormat, ref: ref, onChangeRaw: handleChangeRaw, onChange: handleChange, onBlur: handleBlur, onFocus: handleFocus, 
                        /**
                         * Datepicker triangle is having issues with wide inputs, this is one the workarounds
                         * https://github.com/Hacker0x01/react-datepicker/issues/3176#issuecomment-1262100937
                         */
                        popperModifiers: tslib.__spreadArray(tslib.__spreadArray([], popperModifiers, true), [
                            {
                                name: 'arrow',
                                options: {
                                    padding: function (_a) {
                                        var popper = _a.popper, reference = _a.reference;
                                        return ({
                                            right: Math.min(popper.width, reference.width) - 32
                                        });
                                    }
                                }
                            }
                        ], false), className: "".concat(datePicker_module.default['date-picker__input'], " form-control exclude-global rounded-0 ").concat(errorState ? datePicker_module.default.invalid : '') }, { children: children })), jsxRuntime.jsx(CalendarIcon.default, { className: "".concat(datePicker_module.default['datepicker__icon']) })] })), errorState && (jsxRuntime.jsx(ErrorMessage.default, tslib.__assign({ fieldName: id, errorClassName: cx__default.default({ 'mb-0': helpText }), inputRef: ref, onShow: onError }, { children: errorState }))), helpText && (jsxRuntime.jsx("p", tslib.__assign({ className: cx__default.default(datePicker_module.default['date-picker__help-text'], helpTextClassname) }, { children: helpText })))] })));
};
var DatePicker$1 = DatePicker;

exports.default = DatePicker$1;
//# sourceMappingURL=DatePicker.js.map
